import { formatBytes } from 'shared/utils/format-bytes';
import { theme } from 'styles/theme/theme';
import { UDText } from 'ud-ui/components';

interface FileSizeProps {
  /**
   * Откуда взять вес: `File` — картинку только что выбрали; число — байты уже
   * известны (легаси-дропзона держит картинки своим типом `Image`); адрес
   * строкой или ничего — веса нет.
   */
  value?: File | number | string | null;
  className?: string;
}

/**
 * Вес изображения — подписью под миниатюрой.
 *
 * Нужен, потому что дропзона подменяет файл молча: выбрали фото на 8 МБ, в форму
 * легло сжатое на 3 МБ, и единственный след этого — тост, живущий пять секунд.
 * В форме лежит уже обработанный файл, поэтому подпись показывает ровно тот вес,
 * который уйдёт на бэкенд.
 *
 * Компонент один на обе дропзоны намеренно. До него подпись рисовали два разных
 * куска разметки — `caption-1` серым в новой, `footnote` цветом по умолчанию в
 * легаси, — и одна и та же картинка выглядела подписанной по-разному в
 * зависимости от того, на какой странице её открыли.
 *
 * Решение «что показывать» тоже живёт здесь, а не у вызывающих сторон: у фото,
 * которое уже лежит на бэкенде, в значении поля только адрес, и веса у него нет.
 * Выдумывать его нечем — HEAD-запрос на хранилище стоит лишнего похода в сеть на
 * каждое открытие формы и вдобавок может не пройти CORS. Пустая подпись читается
 * однозначно: она появляется ровно тогда, когда файл заменили.
 */
export const FileSize = ({ value, className }: FileSizeProps) => {
  const bytes = value instanceof File ? value.size : typeof value === 'number' ? value : undefined;

  if (!bytes) return null;

  return (
    <UDText type="caption-1" color={theme.colors.SurfaceRomanSilver30} className={className}>
      {formatBytes(bytes)}
    </UDText>
  );
};
